import type { ComponentProps } from 'svelte'
import type { PermissionCode } from 'utility/permission'
import type { AppContext } from 'types/common'

import DateRange from '@isoftdata/svelte-date-range'
import { AsrNavTabBar } from '@isoftdata/svelte-nav-tab-bar'
import component from './Home.svelte'
import { graphql, type HomeData$result } from '$houdini'
import { datesFromRange, type NamedDateRange } from '@isoftdata/utility-date-time'

export type ChildStates = Array<
	ComponentProps<typeof AsrNavTabBar>['tabs'][number] & { requiredPermission: PermissionCode }
>
export type Dates = ComponentProps<typeof DateRange>['dates']
export type Store = HomeData$result['stores'][number]
export type User = HomeData$result['userAccounts'][number]
export interface Filters {
	userId?: number
	storeId?: number
	dates: {
		from: string
		to: string
	}
}

export const getDisplayStoreName = (store: { name: string; code: string }) => `${store.code} - ${store.name}`
export const calculatePercent = (value: string | number, total: string | number) => {
	return Number(total) ? Math.round((Number(value || '0') / Number(total)) * 100 || 0) : 100
}
export const getDefaultDateRange = (defaultRangeName?: string) => {
	let defaultDateRange: { from: string; to: string }
	try {
		defaultDateRange = datesFromRange(defaultRangeName as NamedDateRange)
	} catch (err) {
		defaultDateRange = datesFromRange('Last 30 Days')
	}
	return defaultDateRange
}

const homeDataQuery = graphql(`
	query HomeData {
		stores {
			id
			name
			code
		}
		userAccounts {
			id
			name
		}
	}
`)

export default function ({ stateRouter, i18next: { t: translate } }: AppContext) {
	stateRouter.addState({
		name: 'app.home',
		route: 'home',
		defaultChild: 'sales-order',
		template: {
			svelte: true,
			component,
		},
		async resolve() {
			const res = await homeDataQuery.fetch()
			const childStates: ChildStates = [
				{
					title: translate('common:salesOrders', 'Sales Orders'),
					name: 'app.home.sales-order',
					parameters: {},
					requiredPermission: 'SALES_ORDERS_CAN_VIEW',
					icon: { icon: 'dollar-sign' },
				},
				{
					title: translate('common:workOrders', 'Work Orders'),
					name: 'app.home.work-order',
					requiredPermission: 'WORK_ORDERS_CAN_VIEW',
					icon: { icon: 'car-wrench' },
				},
				{
					title: translate('common:purchaseOrders', 'Purchase Orders'),
					name: 'app.home.purchase-order',
					requiredPermission: 'PURCHASE_ORDERS_CAN_VIEW',
					icon: { icon: 'box-open' },
				},
				{
					title: translate('common:transferOrders', 'Transfer Orders'),
					name: 'app.home.transfer-order',
					requiredPermission: 'TRANSFER_ORDERS_CAN_VIEW',
					icon: { prefix: 'fak', class: 'fa-solid-warehouse-circle-arrow-left' },
				},
			]

			return Promise.resolve({
				childStates,
				stores: res?.data?.stores ?? [],
				users: res?.data?.userAccounts.sort((a, b) => a.name.localeCompare(b.name)) ?? [],
			})
		},
	})
}
